import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from '../../../utils/axiosConfig';
import logger from '../../../utils/logger';
import Header from '../../shared/components/Common/Header';
import MapComponent from '../../shared/components/Common/MapComponent';
import { getUserLocation } from '../../../services/location';
import Shopping from '../../../assets/shopping.jpg';
import Pickup from '../../../assets/pickup.jpg';
import Cargo from '../../../assets/cargo.jpg';
import { FaUsers } from 'react-icons/fa';
import './ClientDashboard.css'; // Make sure to create this CSS file

const ClientDashboard = () => {
  const [user, setUser] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const initDashboard = async () => {
      try {
        // 1. Store tokens if coming from login redirect
        const urlParams = new URLSearchParams(window.location.search);
        const tokenParam = urlParams.get('token');
        const refreshParam = urlParams.get('refresh');

        if (tokenParam && refreshParam) {
          localStorage.setItem('authToken', tokenParam);
          localStorage.setItem('refreshToken', refreshParam);

          // Clean up URL
          navigate('/client/dashboard', { replace: true });
          return; // Stop here so new page load happens
        }

        // 2. Get token from localStorage
        const token = localStorage.getItem('authToken');
        if (!token) {
          navigate('/login');
          return;
        }

        // 3. Fetch user data
        const config = { headers: { 'Authorization': `Bearer ${token}` } };
        const response = await axios.get('/accounts/user/', config);

        if (response.data) {
          setUser(response.data);

          try {
            const profileResponse = await axios.get('/accounts/profile/', config);
            if (profileResponse.data) {
              setUser(prevUser => ({ ...prevUser, ...profileResponse.data }));
            }
          } catch (profileError) {
            logger.error('Error fetching profile data:', profileError);
            // Keep existing user data even if profile fails
          }
        }

        // Referral data fetching has been moved to the dedicated Referrals page
        
        // We've removed the top-rated riders section as requested
        
        setLoading(false);
      } catch (error) {
        logger.error('Error fetching user data:', error);
        setError(error);

        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          logger.log("Auth token invalid or expired, redirecting to login");
          localStorage.removeItem('authToken');
          localStorage.removeItem('refreshToken');
          navigate('/login');
        } else {
          setLoading(false);
        }
      }
    };

    initDashboard();

    // Fetch user location separately
    getUserLocation()
      .then(location => {
        logger.log("User location retrieved:", location);
        setUserLocation(location);
      })
      .catch(error => {
        logger.error('Error getting user location:', error);
      });
  }, [navigate]);

  const handleSignOut = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        await axios.post('/accounts/logout/', { refresh: refreshToken });
      }
    } catch (error) {
      logger.error('Error during logout:', error);
    } finally {
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      navigate('/login');
    }
  };
  
  // Referral functionality has been moved to the dedicated Referrals page

  const serviceCards = [
    { id: 'shop',    title: 'Shopping',         description: 'Shop for groceries, essentials, and more.',          image: Shopping, link: '/shop' },
    { id: 'parcel',  title: 'Parcel Delivery',  description: 'Fast and reliable parcel pick-up and delivery.',     image: Pickup,   link: '/pickup-delivery' },
    { id: 'cargo',   title: 'Cargo Delivery',   description: 'Deliver large items and cargo to any destination.',  image: Cargo,    link: '/cargo-delivery' },
  ];

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <div className="spinner" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="loading-text">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (error && !user) {
    return (
      <div className="error-container">
        <div className="error-content">
          <h2 className="error-title">Authentication Error</h2>
          <p className="error-message">There was a problem loading your dashboard.</p>
          <button 
            onClick={() => navigate('/login')}
            className="login-button"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      

      <div className="container">
        <div className="dashboard-header">
          <h1 className="dashboard-title">Dashboard</h1>
          <div className="dashboard-nav">
            <Link to="/orders" className="nav-link">Orders</Link>
            <Link to="/notifications" className="nav-link">Notifications</Link>
            <Link to="/referrals" className="nav-link">Referrals</Link>
            <button 
              onClick={handleSignOut}
              className="sign-out-button"
            >
              Sign Out
            </button>
          </div>
        </div>

        <div className="greeting-container">
          <div className="greeting-box">
            <h3 className="greeting-text">Hi {user?.first_name || user?.email?.split('@')[0] || 'User'},</h3>
          </div>
        </div>

        <div className="top-services-grid">
          {serviceCards.map(service => (
            <div key={service.id} className="service-card">
              <div className="service-image-container">
                <img 
                  src={service.image} 
                  alt={service.title}
                  className="service-image"
                />
              </div>
              <div className="service-content">
                <h3 className="service-title">{service.title}</h3>
                <p className="service-description">{service.description}</p>
                <Link
                  to={service.link}
                  className="service-button"
                >
                  Start
                  <svg className="arrow-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                  </svg>
                </Link>
              </div>
            </div>
          ))}
        </div>
        
        {/* Referral Program Section has been moved to a dedicated page and navbar link */}
        
        {/* Top Rated Riders section has been removed as requested */}
      </div>

      <footer className="footer">
        <p>Copyright © Errands 2025 | All rights reserved</p>
      </footer>
    </div>
  );
};

export default ClientDashboard;

